import { z } from 'zod';
import { MOBILE_SCREEN_IDS, type MobileScreenId, type ScreenId } from './registry';

/**
 * Non-mobile screen ids the compatibility floor build's contract already
 * published, before mobile contracts were scoped to mobile screens.
 *
 * Reminder request bodies must keep accepting every value the floor build may
 * send, and a reminder destination comes back in the responses, so these stay
 * in mobile contracts in both directions. The list may only shrink, and
 * removing an id is a request narrowing the client-compat gate reports until
 * the floor moves past a build that no longer knows it. Never add to it: a new
 * web- or Atlas-only screen stays out of every mobile contract.
 */
export const FLOOR_PUBLISHED_NON_MOBILE_SCREEN_IDS = [
  'auth-page',
  'call-staffing',
  'care-outreach-queue',
  'clinician-details',
  'clinicians-list',
  'download',
  'escalation-link',
  'escalation-review',
  'greeting',
  'member-details',
  'member-lookup',
  'members-list',
  'not-found',
  'scheduler-links',
  'signup',
  'staff-profile',
  'telemedicine-appointments',
  'today-view',
] as const satisfies readonly Exclude<ScreenId, MobileScreenId>[];

/**
 * These startup screens are rendered by the installed app, never returned by
 * Home surfaces or reminder destinations. Keeping them out of the existing
 * wire enum lets older installed builds continue parsing those responses.
 */
export const LOCAL_ONLY_MOBILE_SCREEN_IDS = [
  'app-setup',
  'registered-summary',
] as const satisfies readonly MobileScreenId[];

type LocalOnlyMobileScreenId = (typeof LOCAL_ONLY_MOBILE_SCREEN_IDS)[number];

export type MobileContractScreenId =
  | Exclude<MobileScreenId, LocalOnlyMobileScreenId>
  | (typeof FLOOR_PUBLISHED_NON_MOBILE_SCREEN_IDS)[number];

export const MOBILE_CONTRACT_SCREEN_IDS: readonly MobileContractScreenId[] = [
  ...MOBILE_SCREEN_IDS.filter(
    (id): id is Exclude<MobileScreenId, LocalOnlyMobileScreenId> =>
      !LOCAL_ONLY_MOBILE_SCREEN_IDS.some((localId) => localId === id),
  ),
  ...FLOOR_PUBLISHED_NON_MOBILE_SCREEN_IDS,
];

/** The screen ids a contract an installed mobile build sends or parses may carry. */
export const mobileContractScreenIdSchema = z.enum(MOBILE_CONTRACT_SCREEN_IDS);
